/** VersionHistorySidebar — selectable list of versions with active badge + publish action. */

import { useMemo } from 'react';

import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/lib/cn';
import type { VersionInfo } from '../../../services/agentHub';

interface Props {
  versions: VersionInfo[];
  selectedId: string | null;
  onSelect: (versionId: string) => void;
  onActivate: (versionId: string) => void;
  activating: boolean;
  /** Omit to hide the Delete button entirely (e.g. session-context history). */
  onDelete?: (versionId: string) => void;
  deleting?: boolean;
}

function describeVersion(v: VersionInfo): string {
  const ts = new Date(v.created_at).toLocaleString();
  return v.change_summary ? `${ts} — ${v.change_summary}` : ts;
}

export default function VersionHistorySidebar({
  versions,
  selectedId,
  onSelect,
  onActivate,
  activating,
  onDelete,
  deleting,
}: Props) {
  const selectedVersion = useMemo(
    () => versions.find((v) => v.id === selectedId) ?? null,
    [versions, selectedId],
  );
  const showActivate = selectedVersion != null && !selectedVersion.is_active;

  if (versions.length === 0) {
    return (
      <div className="flex w-70 flex-col gap-2">
        <p className="text-sm text-muted-foreground">
          No versions yet. Publish to create the first version.
        </p>
      </div>
    );
  }

  return (
    <div className="flex w-70 flex-col gap-2">
      <h3 className="text-xs font-bold">Version History</h3>
      <ul aria-label="Version history" className="flex flex-col gap-1">
        {versions.map((v) => (
          <li key={v.id}>
            <button
              type="button"
              aria-pressed={selectedId === v.id}
              onClick={() => onSelect(v.id)}
              className={cn(
                'flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent',
                selectedId === v.id && 'bg-accent',
              )}
            >
              <span className="font-medium">
                {`v${v.version_number}${v.is_active ? ' · Active' : ''}`}
              </span>
              <span className="text-xs text-muted-foreground">{describeVersion(v)}</span>
            </button>
          </li>
        ))}
      </ul>
      {showActivate && selectedVersion && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onActivate(selectedVersion.id)}
          disabled={activating}
        >
          {activating ? 'Publishing…' : 'Publish this version'}
        </Button>
      )}
      {onDelete && showActivate && selectedVersion && (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => onDelete(selectedVersion.id)}
          disabled={deleting}
        >
          {deleting ? 'Deleting…' : 'Delete this draft'}
        </Button>
      )}
    </div>
  );
}
